import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { settingsService, SettingsResponse } from '../services/settingsService';
import { customToast } from '../utils/customToast';

interface SettingsContextType {
  settings: SettingsResponse;
  loading: boolean;
  error: string | null;
  updateSetting: (key: string, value: string, type?: string, category?: string) => Promise<void>;
  updateSettings: (settings: { [key: string]: any }) => Promise<void>;
  refreshSettings: () => Promise<void>;
  getSetting: (key: string, defaultValue?: string) => string;
  uploadFavicon: (file: File) => Promise<{ path: string; url: string }>;
  uploadLogo: (file: File) => Promise<{ path: string; url: string }>;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

interface SettingsProviderProps {
  children: ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<SettingsResponse>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const settingsData = await settingsService.getSettings();
      console.log('Settings loaded:', settingsData);
      console.log('Site favicon:', settingsData.site_favicon);
      console.log('Site logo:', settingsData.site_logo);
      console.log('All settings keys:', Object.keys(settingsData));
      setSettings(settingsData);
      
      // Update favicon if it exists
      const favicon = settingsData.site_favicon?.value;
      if (favicon) {
        const apiBase = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';
        const storageBase = apiBase.replace('/api', '/storage');
        const faviconUrl = `${storageBase}/${favicon}`;
        console.log('Loading existing favicon:', faviconUrl);
        updateFaviconInDOM(faviconUrl);
      }
      
      // Update base title if site name exists (for the base app title)
      const siteName = settingsData.site_name?.value;
      
      // If no site_name in settings, try to get shop name from user data
      let titleToSet = siteName;
      if (!titleToSet) {
        try {
          const userDataStr = localStorage.getItem('user_data');
          if (userDataStr) {
            const userData = JSON.parse(userDataStr);
            if (userData.shop?.shop_name) {
              titleToSet = userData.shop.shop_name;
              console.log('Using shop name for title:', titleToSet);
            }
          }
        } catch (err) {
          console.error('Error reading shop name from user data:', err);
        }
      }
      
      if (titleToSet) {
        // Only update title if not on landing page (check by current URL and title)
        const currentTitle = document.title;
        const isLandingPage = window.location.pathname === '/' && 
          (currentTitle.includes('NextGen - Shop - Best POS Software') || 
           currentTitle.includes('Best POS Software & Inventory Management System'));
        
        if (!isLandingPage && (currentTitle.endsWith(' - Management System') || 
            currentTitle === 'NextGen - Shop Management System' ||
            currentTitle === 'NextGen - Shop' ||
            currentTitle.includes('Management System'))) {
          const newTitle = `${titleToSet} - Management System`;
          console.log('Updating title from', currentTitle, 'to', newTitle);
          document.title = newTitle;
        }
      }
    } catch (err) {
      console.error('Failed to load settings:', err);
      setError('Failed to load settings');
      
      // Load default settings
      const defaultSettings = settingsService.getDefaultSettings();
      const formattedDefaults: SettingsResponse = {};
      Object.entries(defaultSettings).forEach(([key, setting]) => {
        formattedDefaults[key] = {
          value: setting.value,
          type: setting.type,
          category: setting.category
        };
      });
      setSettings(formattedDefaults);
    } finally {
      setLoading(false);
    }
  };

  const updateFaviconInDOM = (faviconUrl: string) => {
    console.log('Updating favicon in DOM with URL:', faviconUrl);
    
    // Remove all existing favicon links
    const existingFavicons = document.querySelectorAll('link[rel*="icon"]');
    console.log('Removing existing favicons:', existingFavicons.length);
    existingFavicons.forEach(favicon => favicon.remove());
    
    // Add timestamp to prevent caching
    const timestamp = new Date().getTime();
    const urlWithTimestamp = `${faviconUrl}?v=${timestamp}`;
    console.log('New favicon URL with cache busting:', urlWithTimestamp);
    
    // Create new favicon links with different sizes and types
    const faviconSizes = [
      { rel: 'icon', type: 'image/x-icon', size: null },
      { rel: 'shortcut icon', type: 'image/x-icon', size: null },
      { rel: 'icon', type: 'image/png', size: '16x16' },
      { rel: 'icon', type: 'image/png', size: '32x32' },
      { rel: 'apple-touch-icon', type: 'image/png', size: '180x180' }
    ];
    
    faviconSizes.forEach(({ rel, type, size }) => {
      const link = document.createElement('link');
      link.rel = rel;
      link.type = type;
      link.href = urlWithTimestamp;
      if (size) {
        link.setAttribute('sizes', size);
      }
      document.head.appendChild(link);
    });
    
    // Force browser to refresh favicon
    setTimeout(() => {
      const links = document.querySelectorAll('link[rel*="icon"]');
      links.forEach(link => {
        const href = link.getAttribute('href');
        if (href) {
          link.setAttribute('href', href);
        }
      });
    }, 100);
  };

  const updateSetting = async (key: string, value: string, type: string = 'string', category: string = 'general') => {
    try {
      await settingsService.updateSetting(key, value, type, category);
      
      // Update local state
      setSettings(prev => ({
        ...prev,
        [key]: {
          value,
          type,
          category
        }
      }));

      // Handle special settings
      if (key === 'site_name') {
        document.title = value;
      } else if (key === 'site_favicon' && value) {
        const apiBase = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';
        const storageBase = apiBase.replace('/api', '/storage');
        updateFaviconInDOM(`${storageBase}/${value}`);
      }
    } catch (err) {
      console.error('Failed to update setting:', err);
      throw new Error('Failed to update setting');
    }
  };

  const updateSettings = async (newSettings: { [key: string]: any }) => {
    try {
      const settingsToUpdate: { [key: string]: any } = {};
      
      Object.entries(newSettings).forEach(([key, data]) => {
        if (typeof data === 'string') {
          settingsToUpdate[key] = data;
        } else {
          settingsToUpdate[key] = {
            value: data.value,
            type: data.type || 'string',
            category: data.category || 'general'
          };
        }
      });

      await settingsService.updateSettings(settingsToUpdate);
      
      // Update local state
      setSettings(prev => {
        const updated = { ...prev };
        Object.entries(settingsToUpdate).forEach(([key, data]) => {
          if (typeof data === 'string') {
            updated[key] = {
              value: data,
              type: 'string',
              category: 'general'
            };
          } else {
            updated[key] = {
              value: data.value,
              type: data.type,
              category: data.category
            };
          }
        });
        return updated;
      });

      // Handle special settings
      Object.entries(settingsToUpdate).forEach(([key, data]) => {
        const value = typeof data === 'string' ? data : data.value;
        if (key === 'site_name') {
          document.title = value;
        } else if (key === 'site_favicon' && value) {
          const apiBase = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';
          const storageBase = apiBase.replace('/api', '/storage');
          updateFaviconInDOM(`${storageBase}/${value}`);
        }
      });

      customToast.success('Settings updated successfully');
    } catch (err) {
      console.error('Failed to update settings:', err);
      customToast.error('Failed to update settings');
      throw new Error('Failed to update settings');
    }
  };

  const refreshSettings = async () => {
    console.log('Refreshing settings...');
    await loadSettings();
  };

  const getSetting = (key: string, defaultValue: string = ''): string => {
    // First try to get from settings
    if (settings[key]?.value) {
      return settings[key].value;
    }
    
    // Special handling for favicon and logo - return default paths if not set
    if (key === 'site_favicon' || key === 'site_logo') {
      if (settings[key]?.value) {
        return settings[key].value;
      }
      // Return default asset path
      return 'assets/defaults/Inventro.png';
    }
    
    // If site_name or print_store_name is not in settings, try to get shop name from user data
    if ((key === 'site_name' || key === 'print_store_name') && !settings[key]?.value) {
      try {
        const userDataStr = localStorage.getItem('user_data');
        if (userDataStr) {
          const userData = JSON.parse(userDataStr);
          if (userData.shop?.shop_name) {
            return userData.shop.shop_name;
          }
        }
      } catch (err) {
        console.error('Error reading shop name from user data:', err);
      }
    }
    
    return defaultValue;
  };

  const uploadFavicon = async (file: File): Promise<{ path: string; url: string }> => {
    try {
      console.log('Uploading favicon file:', file.name);
      const result = await settingsService.uploadFavicon(file);
      console.log('Favicon upload result:', result);
      
      // Construct full URL if needed
      const apiBase = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';
      const storageBase = apiBase.replace('/api', '/storage');
      const fullUrl = result.url.startsWith('http') 
        ? result.url 
        : `${storageBase}/${result.path}`;
      
      console.log('Full favicon URL:', fullUrl);
      
      // Update the setting in the database
      await updateSetting('site_favicon', result.path, 'file', 'appearance');
      
      // Update favicon immediately with full URL
      updateFaviconInDOM(fullUrl);
      
      // Also update the initial favicon on page load
      setTimeout(() => {
        updateFaviconInDOM(fullUrl);
      }, 500);
      
      customToast.success('Favicon updated successfully');
      return { ...result, url: fullUrl };
    } catch (err) {
      console.error('Failed to upload favicon:', err);
      customToast.error('Failed to upload favicon');
      throw err;
    }
  };

  const uploadLogo = async (file: File): Promise<{ path: string; url: string }> => {
    try {
      console.log('Uploading logo file:', file.name);
      const result = await settingsService.uploadLogo(file);
      console.log('Logo upload result:', result);
      
      // Construct full URL if needed
      const apiBase = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';
      const storageBase = apiBase.replace('/api', '/storage');
      const fullUrl = result.url.startsWith('http') 
        ? result.url 
        : `${storageBase}/${result.path}`;
      
      console.log('Full logo URL:', fullUrl);
      
      // Update the setting in the database
      await updateSetting('site_logo', result.path, 'file', 'appearance');
      
      customToast.success('Logo updated successfully');
      return { ...result, url: fullUrl };
    } catch (err) {
      console.error('Failed to upload logo:', err);
      customToast.error('Failed to upload logo');
      throw err;
    }
  };

  useEffect(() => {
    // Only load settings if we have an auth token
    const token = localStorage.getItem('auth_token');
    const userDataStr = localStorage.getItem('user_data');
    
    if (token && userDataStr) {
      try {
        const userData = JSON.parse(userDataStr);
        
        // Skip loading settings for super admin (they don't have shop-specific settings)
        if (userData.role === 'super_admin') {
          console.log('Super admin detected, using default settings (no shop settings)');
          const defaultSettings = settingsService.getDefaultSettings();
          const formattedDefaults: SettingsResponse = {};
          Object.entries(defaultSettings).forEach(([key, setting]) => {
            formattedDefaults[key] = {
              value: setting.value,
              type: setting.type,
              category: setting.category
            };
          });
          setSettings(formattedDefaults);
          setLoading(false);
          return;
        }
        
        console.log('Auth token found, loading shop settings...');
        loadSettings();
      } catch (error) {
        console.error('Failed to parse user data, using defaults:', error);
        const defaultSettings = settingsService.getDefaultSettings();
        const formattedDefaults: SettingsResponse = {};
        Object.entries(defaultSettings).forEach(([key, setting]) => {
          formattedDefaults[key] = {
            value: setting.value,
            type: setting.type,
            category: setting.category
          };
        });
        setSettings(formattedDefaults);
        setLoading(false);
      }
    } else {
      console.log('No auth token found, using default settings');
      // Load default settings without making API call
      const defaultSettings = settingsService.getDefaultSettings();
      const formattedDefaults: SettingsResponse = {};
      Object.entries(defaultSettings).forEach(([key, setting]) => {
        formattedDefaults[key] = {
          value: setting.value,
          type: setting.type,
          category: setting.category
        };
      });
      setSettings(formattedDefaults);
      setLoading(false);
    }

    // Listen for auth token availability after login
    const handleAuthTokenAvailable = () => {
      console.log('Auth token available event received, refreshing settings...');
      
      // Check user role before loading settings
      const userDataStr = localStorage.getItem('user_data');
      if (userDataStr) {
        try {
          const userData = JSON.parse(userDataStr);
          if (userData.role === 'super_admin') {
            console.log('Super admin - skipping settings reload');
            return;
          }
        } catch (error) {
          console.error('Failed to parse user data:', error);
        }
      }
      
      loadSettings();
    };

    // Listen for user data updates (e.g., when profile with shop name is updated)
    const handleUserDataUpdated = async () => {
      console.log('User data updated event received, refreshing settings and title...');
      
      // Check user role before loading settings
      const userDataStr = localStorage.getItem('user_data');
      if (userDataStr) {
        try {
          const userData = JSON.parse(userDataStr);
          if (userData.role === 'super_admin') {
            console.log('Super admin - skipping settings reload');
            return;
          }
          
          // Update title immediately with new shop name
          if (userData.shop?.shop_name) {
            const newTitle = `${userData.shop.shop_name} - Management System`;
            console.log('Updating title to:', newTitle);
            document.title = newTitle;
          }
        } catch (error) {
          console.error('Failed to parse user data:', error);
        }
      }
      
      // Force reload settings from server
      await loadSettings();
    };

    window.addEventListener('authTokenAvailable', handleAuthTokenAvailable);
    window.addEventListener('userDataUpdated', handleUserDataUpdated);

    return () => {
      window.removeEventListener('authTokenAvailable', handleAuthTokenAvailable);
      window.removeEventListener('userDataUpdated', handleUserDataUpdated);
    };
  }, []);

  const contextValue: SettingsContextType = {
    settings,
    loading,
    error,
    updateSetting,
    updateSettings,
    refreshSettings,
    getSetting,
    uploadFavicon,
    uploadLogo
  };

  return (
    <SettingsContext.Provider value={contextValue}>
      {children}
    </SettingsContext.Provider>
  );
};